import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PaymentTerms, TermDuration } from '@hero/schema';
import { InvoiceIcon, WalletIcon } from '@hero/web-icons';
import { SelectAuto, type SelectAutoOption } from '../Select/SelectAuto';
import { Label } from '../../ui/Label';
import { describePaymentTerms } from './preview';

/**
 * Canonical preset keys for payment-term durations. The set is shared
 * between invoice issuance and time-to-pay because both sides use the
 * same cadence vocabulary. `manual` exists specifically for invoice
 * issuance — orgs that can't reliably bill on a fixed weekly/monthly
 * cadence (e.g. project-based work) trigger invoicing manually
 * — but is also legal as a `timeToPay` answer when the customer pays
 * on a hand-off rather than on a schedule.
 *
 * Calendar-anchored vs rolling distinction:
 *   - `weekly` / `monthly` are CALENDAR-anchored — issuance falls on
 *     the start of the next week / 1st of the next month (mirrors
 *     `getFirstDayOfNextMonth` + `addToDateV2(..).startOfPeriod` in
 *     `apps/backend/src/app/utils/dates.ts`).
 *   - `days_30` / `days_45` / `days_60` / `days_90` are ROLLING — N
 *     days from the anchor date with no calendar snap. `days_30`
 *     looks similar to `monthly` in days but is NOT the same: a
 *     `monthly` invoice cut on Jan 12 fires next on Feb 1; a
 *     `days_30` one fires next on Feb 11.
 *   - `manual` has no cadence — the user triggers issuance.
 */
type PaymentTermsPreset =
  | 'weekly'
  | 'monthly'
  | 'days_30'
  | 'days_45'
  | 'days_60'
  | 'days_90'
  | 'manual';

const CUSTOM_VALUE = '__custom__';

const PRESET_TO_TERM: Record<PaymentTermsPreset, TermDuration> = {
  weekly: { type: 'week', value: 1 },
  monthly: { type: 'month', value: 1 },
  days_30: { type: 'day', value: 30 },
  days_45: { type: 'day', value: 45 },
  days_60: { type: 'day', value: 60 },
  days_90: { type: 'day', value: 90 },
  manual: { type: 'manual', value: 0 },
};

const PRESET_ORDER: PaymentTermsPreset[] = [
  'weekly',
  'monthly',
  'days_30',
  'days_45',
  'days_60',
  'days_90',
  'manual',
];

/**
 * Map a stored {@link TermDuration} back onto a preset key. Returns
 * `undefined` when the stored value doesn't match any preset (legacy
 * docs that wrote bespoke durations like `{type: 'day', value: 30}`),
 * which lets the caller render a "Custom" option instead of silently
 * collapsing the selection.
 */
function termToPreset(term: TermDuration | undefined): PaymentTermsPreset | undefined {
  if (!term) return undefined;
  for (const key of PRESET_ORDER) {
    const preset = PRESET_TO_TERM[key];
    if (preset.type === term.type && preset.value === term.value) {
      return key;
    }
  }
  return undefined;
}

export interface PaymentTermsFieldsProps {
  /** Currently selected terms. `undefined` shows the placeholder. */
  value: PaymentTerms | undefined;
  /** Called whenever either side (invoiceIssuance / timeToPay) changes. */
  onChange: (next: PaymentTerms) => void;
  /** Disable both selects (e.g. read-only views). */
  disabled?: boolean;
  /** Inline errors keyed by side. */
  errors?: { invoiceIssuance?: string; timeToPay?: string };
  /** Section label rendered above the two selects. Defaults to "Payment Terms". */
  label?: string;
}

/**
 * `PaymentTermsFields` — Shared two-side preset selector for
 * `paymentTermsSchema` (invoice issuance + time to pay).
 *
 * Each side picks from the same fixed list — `weekly`, `monthly`,
 * `45 / 60 / 90 days`, and `manual` — keyed by a stable preset id.
 * The component owns the preset → {@link TermDuration} mapping so
 * callers don't need to think about `{type, value}` pairs; they
 * receive the full `PaymentTerms` snapshot back via `onChange`.
 *
 * `manual` is a real preset (not a sentinel) — it persists as
 * `{type: 'manual', value: 0}` and is most useful for invoice
 * issuance where the org bills on demand rather than on a cadence.
 *
 * Pre-existing docs whose `TermDuration` doesn't match any preset
 * (legacy "30 days" / "10 days" labels) show a non-selectable
 * "Other (X days)" entry that surfaces the actual stored value, so
 * the user understands what's persisted without losing the value on
 * first blur.
 *
 * A short preview sentence below the two selects converts the picked
 * cadences into plain language ("Invoice issued every week. Customer
 * pays 45 days after each invoice.") so the meaning is unambiguous —
 * `Invoice Issuance` / `Time to Pay` on their own can read confusing.
 */
export function PaymentTermsFields({
  value,
  onChange,
  disabled = false,
  errors,
  label,
}: PaymentTermsFieldsProps) {
  const { t } = useTranslation('m-agreement');

  const options: SelectAutoOption[] = useMemo(() => {
    return PRESET_ORDER.map(preset => ({
      value: preset,
      label: t(`form.payment.${preset}.title`, {
        defaultValue: PRESET_FALLBACK_LABEL[preset],
      }),
      description: t(`form.payment.${preset}.description`, {
        defaultValue: PRESET_FALLBACK_DESCRIPTION[preset],
      }),
    }));
  }, [t]);

  const invoiceTerm = value?.invoiceIssuance;
  const payTerm = value?.timeToPay;
  const invoicePreset = termToPreset(invoiceTerm);
  const payPreset = termToPreset(payTerm);

  // When stored data doesn't match any preset, expose a non-selectable
  // "Other (X)" row in that select so the user sees what's persisted.
  const invoiceOptions = invoicePreset
    ? options
    : invoiceTerm
    ? [...options, customOption(t, invoiceTerm)]
    : options;
  const payOptions = payPreset
    ? options
    : payTerm
    ? [...options, customOption(t, payTerm)]
    : options;

  const handleInvoiceChange = (next: string) => {
    if (next === CUSTOM_VALUE) return;
    const term = PRESET_TO_TERM[next as PaymentTermsPreset];
    onChange({
      invoiceIssuance: term,
      timeToPay: payTerm ?? PRESET_TO_TERM.days_45,
    });
  };

  const handlePayChange = (next: string) => {
    if (next === CUSTOM_VALUE) return;
    const term = PRESET_TO_TERM[next as PaymentTermsPreset];
    onChange({
      invoiceIssuance: invoiceTerm ?? PRESET_TO_TERM.weekly,
      timeToPay: term,
    });
  };

  const previewLines = describePaymentTerms(t, value);
  const showPreview = !!(previewLines.invoice || previewLines.pay);

  return (
    <div className="space-y-3">
      {label !== '' && (
        <Label>{label ?? t('form.payment.title', { defaultValue: 'Payment Terms' })}</Label>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <SelectAuto
          options={invoiceOptions}
          value={invoicePreset ?? (invoiceTerm ? CUSTOM_VALUE : undefined)}
          onChange={handleInvoiceChange}
          label={t('form.payment.invoice_issuance.title', { defaultValue: 'Invoice Issuance' })}
          placeholder={t('form.payment.invoice_issuance.placeholder', {
            defaultValue: 'When do we issue the invoice?',
          })}
          disabled={disabled}
          error={errors?.invoiceIssuance}
        />
        <SelectAuto
          options={payOptions}
          value={payPreset ?? (payTerm ? CUSTOM_VALUE : undefined)}
          onChange={handlePayChange}
          label={t('form.payment.time_to_pay.title', { defaultValue: 'Time to Pay' })}
          placeholder={t('form.payment.time_to_pay.placeholder', {
            defaultValue: 'When does the customer pay?',
          })}
          disabled={disabled}
          error={errors?.timeToPay}
        />
      </div>
      {showPreview && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {previewLines.invoice && (
            <div className="flex items-start gap-2 rounded-md border border-indigo-100 bg-indigo-50/60 px-3 py-2">
              <InvoiceIcon className="size-4 text-indigo-600 shrink-0 mt-0.5" />
              <span className="text-xs leading-relaxed text-indigo-900">
                {previewLines.invoice}
              </span>
            </div>
          )}
          {previewLines.pay && (
            <div className="flex items-start gap-2 rounded-md border border-emerald-100 bg-emerald-50/60 px-3 py-2">
              <WalletIcon className="size-4 text-emerald-600 shrink-0 mt-0.5" />
              <span className="text-xs leading-relaxed text-emerald-900">
                {previewLines.pay}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const PRESET_FALLBACK_LABEL: Record<PaymentTermsPreset, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  days_30: '30 Days',
  days_45: '45 Days',
  days_60: '60 Days',
  days_90: '90 Days',
  manual: 'Manual',
};

const PRESET_FALLBACK_DESCRIPTION: Record<PaymentTermsPreset, string> = {
  weekly: 'Beginning of every week',
  monthly: '1st of every month',
  days_30: 'Every 30 days from the anchor date',
  days_45: 'Every 45 days from the anchor date',
  days_60: 'Every 60 days from the anchor date',
  days_90: 'Every 90 days from the anchor date',
  manual: 'Triggered manually whenever needed',
};

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Build a non-selectable "Other (…)" option that surfaces the actual
 * stored {@link TermDuration} when it doesn't match any preset.
 * Keeping the value visible (e.g. "Other — 30 days") prevents the
 * user from being confused by a generic "Custom" label and makes
 * legacy data discoverable in the UI.
 */
function customOption(t: TFn, term: TermDuration): SelectAutoOption {
  const fallback = formatTerm(term);
  return {
    value: CUSTOM_VALUE,
    label: t('form.payment.custom.title', {
      term: fallback,
      defaultValue: `Other — ${fallback}`,
    }),
    description: t('form.payment.custom.description', {
      defaultValue: 'Stored value does not match any preset',
    }),
    disabled: true,
  };
}

function formatTerm(term: TermDuration): string {
  const v = term.value || 0;
  switch (term.type) {
    case 'manual':
      return 'manual';
    case 'immediate':
      return 'same day';
    case 'week':
      return v === 1 ? '1 week' : `${v} weeks`;
    case 'month':
      return v === 1 ? '1 month' : `${v} months`;
    case 'day':
    default:
      return v === 1 ? '1 day' : `${v} days`;
  }
}
